
























































import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { BusEvent } from '@/models/bus';
import { MediaFileField } from '../models/fields';

import { loadFile } from '@/util/file';

import Media from '../api/media';
import EditIcon from '@/components/icons/EditIcon.vue';

@Component({
  components: {
    'edit-icon': EditIcon
  }
})
export default class FieldAudio extends Vue implements MediaFileField {
  @Prop({ type: String }) public src?: string;
  @Prop({ type: Boolean, default: false }) public disabled?: boolean;

  // allow the user to unselect the audio after being chosen
  @Prop({ type: Boolean, default: true }) public allowUnselection!: boolean;
  @Prop({ type: File, default: null }) public initialFile!: File | null;

  // used so inputs don't interfere with each other
  public static counter: number = 0;
  public uniqueId: string = 'field-audio' + FieldAudio.counter++;

  public file: File | null = this.initialFile;
  public preview: any = null;
  public changed: boolean = false;

  public async beforeMount() {
    await this.updatePreview();
  }

  @Watch('file')
  public async updatePreview() {
    if (this.file && !this.preview) {
      this.preview = await loadFile(this.file);
    }
  }

  @Watch('src')
  public updateId() {
    this.uniqueId = 'field-audio' + FieldAudio.counter++;
  }

  public async onFileChange(name: string, files: FileList): Promise<void> {
    if (files.length === 0) {
      this.file = null;
      this.preview = null;
    } else {
      const file = files.item(0);
      if (file === null) {
        return;
      }
      if (/\.(mp3)$/i.test(file.name)) {
        await this.clear();
        this.file = file;
        // used when file is not immediately uploaded (eg: FieldHints)
        this.$emit('NewFile', file);
        this.changed = true;
      } else {
        this.$bus.$emit(
          BusEvent.ERROR,
          'Audio Error',
          new Error(`Unsupported video format: ${file.name}. Please use .mp3`)
        );
      }
    }
  }

  public toggleAudio(event) {
    if (event.currentTarget.paused) {
      event.currentTarget.play();
    } else {
      event.currentTarget.pause();
    }
  }

  public getFileName() {
    if (this.file) {
      return this.file.name;
    } else {
      return 'No new file';
    }
  }

  /**
   * Upload the audio.
   * Will throw an error if there is nothing to upload.
   *
   * @param {string} parentModel
   *     Parent model slug, e.g. Exercice.SLUG.
   * @param {number} parentId
   *     Parent object identifier.
   *     This means you have to create the parent entity on the server
   *     before you can call this method!
   * @param {string} fieldName
   *     e.g. bg_image icon
   * @returns {Promise<number>}
   *     Identifier of a Media entity.
   */
  public async upload(
    parentModel: string,
    parentId: number,
    fieldName: string
  ): Promise<number> {
    if (!parentModel || parentId === null) {
      // Uploading files is only possible after the parent entity has been created
      throw Error('Missing parent information.');
    }

    if (!this.hasNewMedia) {
      throw Error('Audio has not changed, nothing to upload.');
    }

    return await Media.createAudio(this.file, parentModel, parentId, fieldName);
  }

  public async clear(e?) {
    if (e) {
      e.stopPropagation();
    }
    if (!this.disabled) {
      // only emit  delete event the first time the cross is clicked
      if (!this.changed && this.src) {
        this.$emit('DeleteMedia', 'video');
      }

      this.changed = true;
      this.file = null;
      this.preview = null;
    }
  }

  // if it has media in the field
  public hasMedia(): boolean {
    return this.hasNewMedia() || (this.src && !this.changed);
  }

  // if it has new media in the field to be uploaded
  public hasNewMedia(): boolean {
    return !!this.file;
  }

  public inputClicked() {
    this.$emit('InputClicked');
  }
}
